import { useCallback, useEffect, useRef } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  NotesThreadResponseSchema,
  normalizeAuthorName,
  normalizeNoteText,
  type NoteMention,
  type NoteTarget,
  type NotesThreadResponse,
  noteTargetKey,
} from '@shared/flow/notes';
import { useApi } from '../../../../lib/api';
import { fetchJson } from '../../../../lib/apiFetchJson';
import { backgroundWrite } from '../../../../lib/background-write';
import { ApiProblemError, WriteRefusedError } from '../../../../lib/apiProblemError';
import { useNotesEnabled } from '../../../../hooks/useNotesEnabled';
import { useFlowIdentity } from '../../useFlowIdentity';
import { useFlowNotesStore, type NotesPending } from './flow-notes-store';
import { FLOW_NOTES_KEY, FLOW_NOTES_SUMMARIES_KEY } from './notes-query-keys';

/** Thread cache key `['flow-notes', accountId, kind, id]`. `accountId` leads so one prefix covers an account and its contacts. */
export function notesThreadKey(target: NoteTarget) {
  return [...FLOW_NOTES_KEY, target.accountId, target.kind, target.id] as const;
}

/** Same key + same `queryFn` for every observer of a thread, so no observer can clobber another's fetcher. */
function useNotesThreadOptions(target: NoteTarget | null) {
  const { apiFetch } = useApi();
  return queryOptions<NotesThreadResponse>({
    queryKey: target ? notesThreadKey(target) : [...FLOW_NOTES_KEY, 'none'],
    staleTime: Infinity,
    // The pane renders its own error, so the global toast is suppressed.
    meta: { suppressErrorToast: true },
    queryFn: () => {
      if (!target) throw new Error('Notes thread queryFn ran without a target');
      const params = new URLSearchParams({ kind: target.kind, id: target.id });
      return fetchJson(apiFetch, NotesThreadResponseSchema, `/api/flow/notes/thread?${params.toString()}`);
    },
  });
}

/**
 * The live Notes thread for one target (`GET /api/flow/notes/thread`). Fresh
 * forever (SSE and post confirmation invalidate it), re-read on every mount AND
 * whenever the mounted hook switches target, so opening a thread always shows
 * Salesforce's current text (`refetchOnMount` alone misses A -> B -> A on one
 * mounted pane). Sends nothing for a null target or while notes are disabled.
 */
export function useFlowNotesThread(target: NoteTarget | null) {
  const { enabled } = useNotesEnabled();
  const queryClient = useQueryClient();
  const query = useQuery({ ...useNotesThreadOptions(target), enabled: !!target && enabled, refetchOnMount: 'always' });
  // Primitives, not the target object (a new object on many renders).
  const kind = target?.kind;
  const id = target?.id;
  const accountId = target?.accountId ?? null;
  useEffect(() => {
    if (!kind || !id || !enabled) return;
    // `cancelRefetch: false` joins the mount fetch (and StrictMode's replay) instead of starting a second.
    void queryClient.refetchQueries({ queryKey: notesThreadKey({ kind, id, accountId }), exact: true }, { cancelRefetch: false });
  }, [kind, id, accountId, enabled, queryClient]);

  // A contact opened without an account id (the mirror lacks it) gets the one the live read found, so
  // the @ picker and the Posting to list work. The cache entry is copied to the new key first so the
  // pane does not fall back to "Loading notes" (which would unmount the composer). The re-key makes
  // the observer start a second GET for the account-less contact's new key; that is expected.
  const resolvedAccountId = query.data?.target.accountId ?? null;
  const needsAccountId =
    target !== null && target.accountId === null && query.data?.target.kind === target.kind && query.data.target.id === target.id;
  useEffect(() => {
    if (!needsAccountId || !target || !resolvedAccountId || !query.data) return;
    queryClient.setQueryData(notesThreadKey({ ...target, accountId: resolvedAccountId }), query.data);
    useFlowNotesStore.getState().adoptAccountId(target, resolvedAccountId);
  }, [needsAccountId, target, resolvedAccountId, query.data, queryClient]);
  return query;
}

/**
 * The acting user's Salesforce display name: the author string the server
 * writes into terra history lines and the "mine" comparison reads. Shares the
 * `['flow-identity']` query; no second `/api/flow/identity` request.
 */
export function useMyNoteIdentity(): string | null {
  return useFlowIdentity().data?.sfDisplayName ?? null;
}

export interface PostNoteInput {
  target: NoteTarget;
  notes: string;
  /** One uuid per attempt; `retry()` re-sends the same one so the server can dedupe. */
  clientNoteId: string;
  mentions: NoteMention[];
  source?: 'composer' | 'save_as_note';
}

/** `{accounts|contacts}/:id/notes` PATCH route for a target. */
function notesPatchUrl(target: NoteTarget): string {
  return `/api/flow/${target.kind === 'account' ? 'accounts' : 'contacts'}/${target.id}/notes`;
}

/** Entries the target record has in any cached thread under its account (max across threads). */
function cachedRecordCount(
  queryClient: ReturnType<typeof useQueryClient>,
  target: NoteTarget,
): number | null {
  const threads = queryClient.getQueriesData<NotesThreadResponse>({ queryKey: [...FLOW_NOTES_KEY, target.accountId] });
  let best: number | null = null;
  for (const [, data] of threads) {
    if (!data) continue;
    const n = data.entries.filter(e => e.recordKind === target.kind && e.recordId === target.id).length;
    best = best === null ? n : Math.max(best, n);
  }
  return best;
}

/** Server code for "this text is already the last line of the note". */
const NOTE_UNCHANGED_CODE = 'NOTE_UNCHANGED';

/** Shown only when a non-problem error (a thrown bug) reaches the inline slot. */
const POST_FAILED_FALLBACK = "Couldn't save the note. Please try again.";

/**
 * The rep-facing outcome of a rejected PATCH. Never a status code or machine
 * code: a `WriteRefusedError` carries its own sentence (its inherited `title`
 * can be the sandbox's), other problems prefer the server `title`.
 */
function postFailure(err: unknown): Pick<NotesPending, 'status' | 'error'> {
  if (err instanceof ApiProblemError && err.code === NOTE_UNCHANGED_CODE) return { status: 'unchanged' };
  if (err instanceof WriteRefusedError) return { status: 'failed', error: { title: err.userMessage } };
  if (err instanceof ApiProblemError) {
    return { status: 'failed', error: { title: err.title ?? err.userMessage ?? err.message, ...(err.oauthUrl ? { oauthUrl: err.oauthUrl } : {}) } };
  }
  return { status: 'failed', error: { title: POST_FAILED_FALLBACK } };
}

/**
 * Posts a note (`PATCH .../notes`). The pending bubble lives in
 * `useFlowNotesStore`, never in the query cache (no optimistic thread entry);
 * `useNotesPendingConfirmation` turns "accepted" into "confirmed". One
 * in-flight post per tab: a second `mutate` while `saving` is ignored.
 * Cross-tab exclusion is out of scope (the server guards per request path).
 */
export function usePostNote() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { enabled } = useNotesEnabled();
  // Per-attempt acceptance callbacks, fired from the hook-level onSuccess so they still run when the
  // caller unmounted mid-request (pane switch). They run against a dead component, so they may only
  // touch module-scope state (the Notes store): any component-local copy of the draft would be
  // left stale in a newly mounted instance.
  const acceptedRef = useRef(new Map<string, () => void>());

  const mutation = useMutation<unknown, unknown, PostNoteInput>({
    // The pending bubble renders the error inline.
    meta: { suppressErrorToast: true },
    mutationFn: input =>
      fetchJson(apiFetch, z.unknown(), notesPatchUrl(input.target), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: input.notes,
          clientNoteId: input.clientNoteId,
          mentions: input.mentions,
          ...(input.source ? { source: input.source } : {}),
        }),
      }),
    onSuccess: (_data, input) => {
      const onAccepted = acceptedRef.current.get(input.clientNoteId);
      acceptedRef.current.delete(input.clientNoteId);
      onAccepted?.();
      const { pending, setPending } = useFlowNotesStore.getState();
      // A replaced slot (newer post) must not be touched by an older response.
      if (pending?.clientNoteId !== input.clientNoteId) return;
      setPending({ ...pending, status: 'awaiting' });
      void queryClient.invalidateQueries({ queryKey: [...FLOW_NOTES_KEY, input.target.accountId] });
    },
    onError: (err, input) => {
      // The callback is kept for a `retry()` (same clientNoteId); `mutate` drops it when a new post replaces this one.
      const { pending, setPending } = useFlowNotesStore.getState();
      if (pending?.clientNoteId !== input.clientNoteId) return;
      setPending({ ...pending, ...postFailure(err) });
    },
  });
  const { mutate: rawMutate } = mutation;

  const mutate = useCallback(
    (input: PostNoteInput, opts?: { onAccepted?: () => void }) => {
      if (!enabled) return;
      const { pending, setPending } = useFlowNotesStore.getState();
      if (pending?.status === 'saving') return;
      // A new post replacing a failed/unchanged one must not lose the rep's
      // words: put the old body back when its draft is empty (retry reuses the id).
      if ((pending?.status === 'failed' || pending?.status === 'unchanged') && pending.clientNoteId !== input.clientNoteId) {
        const { drafts, setDraft, setDraftMentions } = useFlowNotesStore.getState();
        const key = noteTargetKey(pending.target);
        if (!drafts[key]) {
          setDraft(key, pending.body);
          if (pending.mentions?.length) setDraftMentions(key, pending.mentions);
        }
      }
      const isRetry = pending?.clientNoteId === input.clientNoteId;
      if (pending && !isRetry) acceptedRef.current.delete(pending.clientNoteId);
      if (opts?.onAccepted) acceptedRef.current.set(input.clientNoteId, opts.onAccepted);
      const next: NotesPending = {
        clientNoteId: input.clientNoteId,
        target: input.target,
        body: input.notes,
        at: new Date().toISOString(),
        status: 'saving',
        mentions: input.mentions,
        source: input.source,
        // A retry keeps the first attempt's baseline: the cache may already hold a line that landed.
        baselineCount: isRetry ? pending.baselineCount : cachedRecordCount(queryClient, input.target),
      };
      setPending(next);
      rawMutate(input);
    },
    [enabled, queryClient, rawMutate],
  );

  /** Re-sends the failed (or unchanged) post with the SAME `clientNoteId`. */
  const retry = useCallback(() => {
    const { pending } = useFlowNotesStore.getState();
    if (!pending || (pending.status !== 'failed' && pending.status !== 'unchanged')) return;
    mutate({
      target: pending.target,
      notes: pending.body,
      clientNoteId: pending.clientNoteId,
      mentions: pending.mentions ?? [],
      source: pending.source,
    });
  }, [mutate]);

  return { mutate, retry, isPending: mutation.isPending };
}

/**
 * Delays, in ms after the server accepted a post, at which the thread is
 * re-read to confirm the line reached Salesforce history. These are bounded
 * confirmation re-checks, not a cache TTL and not polling: after the last one
 * the pending item is marked not confirmed and nothing else is scheduled.
 */
const CONFIRMATION_RECHECK_DELAYS_MS = [3_000, 10_000, 30_000] as const;

/** How far before the post an exact match may be stamped and still confirm it when the baseline is unknown. */
const UNKNOWN_BASELINE_WINDOW_MS = 5 * 60_000;

/**
 * True when `thread` holds the pending post as a NEW history line: the posted-to
 * record has more entries than before the post AND one of the lines added since is a terra
 * line by me (normalized name) for the same record with the same normalized text. Growth is what
 * keeps an identical historical line (or a shifted `lineIndex`) from confirming.
 */
function threadConfirmsPending(thread: NotesThreadResponse, pending: NotesPending, myName: string): boolean {
  const { target } = pending;
  const entries = thread.entries.filter(e => e.recordKind === target.kind && e.recordId === target.id);
  if (pending.baselineCount != null && entries.length <= pending.baselineCount) return false;
  // Entries are newest first, so the lines added since the post are the head;
  // a teammate's line landing in the same refresh may sit above mine.
  const added = pending.baselineCount == null ? entries : entries.slice(0, entries.length - pending.baselineCount);
  // Unknown baseline (nothing cached when posting): the exact match must also be stamped around the
  // post, so an identical older line of mine cannot confirm it. The window absorbs the stamp's
  // minute resolution and client/server clock skew.
  const earliestMs = pending.baselineCount == null ? Date.parse(pending.at) - UNKNOWN_BASELINE_WINDOW_MS : Number.NEGATIVE_INFINITY;
  const me = normalizeAuthorName(myName);
  const body = normalizeNoteText(pending.body);
  return added.some(
    e => e.format === 'terra' && e.author === me && normalizeNoteText(e.body) === body && (!Number.isFinite(earliestMs) || (!!e.at && Date.parse(e.at) >= earliestMs)),
  );
}

/**
 * Turns "the server accepted my post" into "Salesforce history shows it".
 * Mount ONCE (RailDock), not in the composer or a popover: it owns the
 * confirmation re-check timers for `pending`, keyed by its `clientNoteId`.
 * Any successful thread refresh (these re-checks, an SSE invalidation, the
 * pane reopening) can confirm. Confirming clears `pending` only if it still
 * is the same post, so a late response never touches a replacement.
 */
export function useNotesPendingConfirmation(): void {
  const queryClient = useQueryClient();
  const myName = useMyNoteIdentity();
  const awaitingId = useFlowNotesStore(s => (s.pending?.status === 'awaiting' ? s.pending.clientNoteId : null));

  useEffect(() => {
    if (!myName) return;
    return queryClient.getQueryCache().subscribe(event => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      const { pending, setPending } = useFlowNotesStore.getState();
      if (pending?.status !== 'awaiting') return;
      const threads = queryClient.getQueriesData<NotesThreadResponse>({
        queryKey: [...FLOW_NOTES_KEY, pending.target.accountId],
      });
      if (!threads.some(([, data]) => data && threadConfirmsPending(data, pending, myName))) return;
      setPending(null);
      useFlowNotesStore.setState({ lastConfirmed: { clientNoteId: pending.clientNoteId, target: pending.target } });
    });
  }, [queryClient, myName]);

  useEffect(() => {
    if (!awaitingId) return;
    // Local to this effect run (not a ref): StrictMode's replay and a real
    // remount each get their own flag, so nothing is stuck cancelled or doubled.
    let cancelled = false;
    const stillAwaiting = () => {
      const pending = useFlowNotesStore.getState().pending;
      return !cancelled && pending?.clientNoteId === awaitingId && pending.status === 'awaiting';
    };
    const timers = CONFIRMATION_RECHECK_DELAYS_MS.map((delay, i) =>
      setTimeout(() => {
        if (!stillAwaiting()) return;
        void (async () => {
          const { pending } = useFlowNotesStore.getState();
          const accountId = pending?.target.accountId ?? null;
          // A failed re-check is just "not confirmed yet".
          await queryClient.refetchQueries({ queryKey: [...FLOW_NOTES_KEY, accountId] }).catch(() => undefined);
          if (i !== CONFIRMATION_RECHECK_DELAYS_MS.length - 1 || !stillAwaiting()) return;
          const current = useFlowNotesStore.getState().pending;
          if (current) useFlowNotesStore.getState().setPending({ ...current, notConfirmed: true });
        })();
      }, delay),
    );
    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
    };
  }, [awaitingId, queryClient]);
}

/**
 * Marks the open thread read (`POST /api/flow/notes/read`) once the thread has
 * loaded, and again when my own post is confirmed. Never on PATCH acceptance
 * (history is not there yet) and never before the read succeeded (a failed
 * thread was not "viewed"). Off while notes are disabled. It is a background
 * write: it resolves `null` without sending while impersonating, and a refused
 * write is silent, so it can neither change the target user's state nor toast.
 */
export function useMarkRead(target: NoteTarget | null): void {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const { enabled } = useNotesEnabled();
  // Observe only: `enabled: false` never fetches (the pane's own
  // `useFlowNotesThread` owns the request), it just reports the cache state.
  const threadQuery = useQuery({ ...useNotesThreadOptions(target), enabled: false });
  const confirmedId = useFlowNotesStore(s => s.lastConfirmed?.clientNoteId ?? null);

  const { mutate } = useMutation<Response | null, unknown, NoteTarget>({
    meta: { suppressErrorToast: true },
    mutationFn: body =>
      backgroundWrite(apiFetch, '/api/flow/notes/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: response => {
      if (response?.ok) void queryClient.invalidateQueries({ queryKey: FLOW_NOTES_SUMMARIES_KEY });
    },
  });

  // Dedupe token, deliberately NOT reset on mount: StrictMode's replayed effect
  // must not send twice; a real remount is a new hook instance with a fresh ref.
  const lastSentRef = useRef<string | null>(null);
  // Cached data is not "viewed": wait for a fetch after this thread was opened
  // (the pane's mount refetch) to land, i.e. `dataUpdatedAt` moved off the value
  // the cache held when the thread opened.
  const targetKey = target ? noteTargetKey(target) : null;
  const updatedAtOnOpen = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });
  if (updatedAtOnOpen.current.key !== targetKey) updatedAtOnOpen.current = { key: targetKey, at: threadQuery.dataUpdatedAt };
  const loaded =
    threadQuery.status === 'success' && !threadQuery.isFetching && threadQuery.dataUpdatedAt !== updatedAtOnOpen.current.at;
  useEffect(() => {
    if (!enabled || !target || !loaded) return;
    const token = `${noteTargetKey(target)}|${confirmedId ?? ''}`;
    if (lastSentRef.current === token) return;
    lastSentRef.current = token;
    mutate(target);
  }, [enabled, target, loaded, confirmedId, mutate]);
}
